import type { PatientWorkspaceViewModel } from "../types.js";

const weekdayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function parseIsoDate(isoDate: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);

  if (!match) {
    return null;
  }

  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
}

/** "Mon Jul 14" style header for a day bucket. */
export function formatDayHeaderText(isoDate: string): string {
  const date = parseIsoDate(isoDate);

  if (!date) {
    return isoDate;
  }

  return `${weekdayNames[date.getUTCDay()]} ${monthNames[date.getUTCMonth()]!.slice(0, 3)} ${String(date.getUTCDate())}`;
}

export function formatMonthTitleText(year: number, monthIndex: number): string {
  return `${monthNames[monthIndex]} ${String(year)}`;
}

export interface PatientDayBucket {
  isoDate: string | null;
  headerText: string;
  patients: PatientWorkspaceViewModel[];
}

/**
 * Buckets patients by date of surgery, soonest first. Patients without a
 * DOS go into a final "No surgery date" bucket — never hidden.
 */
export function groupPatientsByDateOfService(
  patients: PatientWorkspaceViewModel[],
): PatientDayBucket[] {
  const byDate = new Map<string, PatientWorkspaceViewModel[]>();
  const undated: PatientWorkspaceViewModel[] = [];

  for (const patient of patients) {
    if (patient.dateOfService == null) {
      undated.push(patient);
      continue;
    }

    const bucket = byDate.get(patient.dateOfService) ?? [];

    bucket.push(patient);
    byDate.set(patient.dateOfService, bucket);
  }

  const buckets: PatientDayBucket[] = [...byDate.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([isoDate, bucketPatients]) => ({
      isoDate,
      headerText: `${formatDayHeaderText(isoDate)} — ${String(bucketPatients.length)} patient${bucketPatients.length === 1 ? "" : "s"}`,
      patients: bucketPatients,
    }));

  if (undated.length > 0) {
    buckets.push({
      isoDate: null,
      headerText: `No surgery date — ${String(undated.length)} patient${undated.length === 1 ? "" : "s"}`,
      patients: undated,
    });
  }

  return buckets;
}

export interface MonthGridCell {
  isoDate: string;
  dayNumber: number;
  inMonth: boolean;
}

/**
 * 6x7 month grid (weeks start Sunday), padded with the neighboring months'
 * days so the layout is stable across months.
 */
export function buildMonthGrid(
  year: number,
  monthIndex: number,
): MonthGridCell[][] {
  const firstOfMonth = new Date(Date.UTC(year, monthIndex, 1));
  const start = new Date(firstOfMonth);

  start.setUTCDate(start.getUTCDate() - firstOfMonth.getUTCDay());

  const weeks: MonthGridCell[][] = [];
  const cursor = new Date(start);

  for (let week = 0; week < 6; week += 1) {
    const cells: MonthGridCell[] = [];

    for (let day = 0; day < 7; day += 1) {
      cells.push({
        isoDate: cursor.toISOString().slice(0, 10),
        dayNumber: cursor.getUTCDate(),
        inMonth: cursor.getUTCMonth() === monthIndex,
      });
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }

    weeks.push(cells);
  }

  return weeks;
}

export function countPatientsByDay(
  patients: PatientWorkspaceViewModel[],
): Map<string, number> {
  const counts = new Map<string, number>();

  for (const patient of patients) {
    if (patient.dateOfService != null) {
      counts.set(
        patient.dateOfService,
        (counts.get(patient.dateOfService) ?? 0) + 1,
      );
    }
  }

  return counts;
}

export interface CalendarRange {
  start: string;
  end: string;
}

export interface CalendarTapResult {
  range: CalendarRange | null;
  pendingStart: string | null;
}

/**
 * Two-tap range selection state machine. A single day is the degenerate
 * range (start === end); tapping earlier than the pending start swaps the
 * ends so nurses never have to think about tap order.
 */
export function applyCalendarDayTap(
  range: CalendarRange | null,
  pendingStart: string | null,
  tappedIsoDate: string,
): CalendarTapResult {
  if (pendingStart != null) {
    if (tappedIsoDate === pendingStart) {
      return { range: null, pendingStart: null };
    }

    return {
      range:
        tappedIsoDate > pendingStart
          ? { start: pendingStart, end: tappedIsoDate }
          : { start: tappedIsoDate, end: pendingStart },
      pendingStart: null,
    };
  }

  return { range: null, pendingStart: tappedIsoDate };
}

export function isDateInRange(
  isoDate: string,
  range: CalendarRange,
): boolean {
  return isoDate >= range.start && isoDate <= range.end;
}

export interface CalendarPresetRange {
  key: string;
  label: string;
  range: CalendarRange;
}

function shiftIsoDate(isoDate: string, offsetDays: number): string {
  const date = parseIsoDate(isoDate);

  if (!date) {
    return isoDate;
  }

  date.setUTCDate(date.getUTCDate() + offsetDays);

  return date.toISOString().slice(0, 10);
}

/** Preset spans. Weeks run Sunday-Saturday, matching the month grid. */
export function buildPresetRanges(todayIso: string): CalendarPresetRange[] {
  const today = parseIsoDate(todayIso);

  if (!today) {
    return [];
  }

  const weekStart = shiftIsoDate(todayIso, -today.getUTCDay());
  const monthStart = `${todayIso.slice(0, 8)}01`;
  const monthEnd = shiftIsoDate(
    `${new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, 1))
      .toISOString()
      .slice(0, 10)}`,
    -1,
  );

  return [
    { key: "today", label: "Today", range: { start: todayIso, end: todayIso } },
    {
      key: "next-7",
      label: "7 days",
      range: { start: todayIso, end: shiftIsoDate(todayIso, 6) },
    },
    {
      key: "this-week",
      label: "This wk",
      range: { start: weekStart, end: shiftIsoDate(weekStart, 6) },
    },
    {
      key: "next-week",
      label: "Next wk",
      range: {
        start: shiftIsoDate(weekStart, 7),
        end: shiftIsoDate(weekStart, 13),
      },
    },
    {
      key: "this-month",
      label: "Month",
      range: { start: monthStart, end: monthEnd },
    },
  ];
}

export function formatRangeLabelText(range: CalendarRange): string {
  if (range.start === range.end) {
    return formatDayHeaderText(range.start);
  }

  return `${formatDayHeaderText(range.start)} – ${formatDayHeaderText(range.end)}`;
}
